import { consoleLoggingIntegration, init } from '@sentry/nextjs';

type Parameters<T extends (args: never) => unknown> = T extends (
  ...args: infer P
) => unknown
  ? P
  : never;

/**
 * Error patterns from browser extensions, stale deploys, and transient
 * network issues that are not actionable and should be dropped.
 */
const IGNORED_CLIENT_ERRORS: readonly (string | RegExp)[] = [
  // --- Browser extension noise ---
  '__firefox__',
  'window.webkit.messageHandlers',
  "Can't find variable: DarkReader",
  'apple-pay-merchandising-modal',

  // --- Stale client after deployment ---
  /Failed to load chunk/i,

  // --- DOM hydration race conditions ---
  'removeChild',
  'The node to be removed is not a child of this node',
  'The object can not be found here',

  // --- Transient network errors (client-side) ---
  'Load failed',
  'TypeError',
  'Failed to fetch',
  'Network request failed',
  'network error',
  /NetworkError/i,
  'AuthRetryableFetchError',

  // --- Browser Engine Noise ---
  'ResizeObserver loop limit exceeded',
  'ResizeObserver loop completed with undelivered notifications',
  'Maximum call stack size exceeded',

  // --- Native/webview bridge noise ---
  'Error invoking enableDidUserTypeOnKeyboardLogging: Java object is gone',
  "Can't find variable: SCDynimacBridge",

  // --- Expected auth validations surfaced to users ---
  'Invalid login credentials',
  'Email not confirmed',
  'Token has expired or is invalid',
  'New password should be different from the old password',
  'Email sign-in failed',
  'Phone number already registered by another user',

  // --- Moyasar SDK double-init ---
  'Page already has an active payment session',

  // --- Payment declines surfaced client-side ---
  /Apple Pay.*(?:INSUFFICIENT FUNDS|EXCEEDS WITHDRAWAL LIMIT|TIMED_OUT|DECLINED)/i,
  'Apple Pay sheet dismissed',

  // --- InvalidAccessError from Moyasar/payment SDKs ---
  'The object does not support the operation or argument',

  // --- Stripe SDK Noise ---
  'You cannot have multiple Embedded Checkout objects',

  // --- Next.js redundant generic errors ---
  // When a Server Component or Action throws an error, Next.js logs the real
  // error on the server but sends these generic obfuscated errors to the client.
  // We ignore them on the client to avoid duplicate noise (since the real error
  // is already captured server-side).
  'An error occurred in the Server Components render',
  'An unexpected response was received from the server.',
];
function isIgnoredClientError(text: string | undefined): boolean {
  if (!text) return false;

  return IGNORED_CLIENT_ERRORS.some((pattern) =>
    typeof pattern === 'string' ? text.includes(pattern) : pattern.test(text),
  );
}

/**
 * @name initializeSentryBrowserClient
 * @description Initialize the Sentry client
 * @param props
 */
export function initializeSentryBrowserClient(
  props: Parameters<typeof init>[0] = {},
) {
  return init({
    dsn: process.env.NEXT_PUBLIC_SENTRY_DSN,

    // Enable logs to be sent to Sentry (requires SDK 9.12.0+)
    enableLogs: true,

    // Integrations
    integrations: [
      // Keep console forwarding limited to errors. Routine log/info/warn output
      // is high-volume and rarely actionable once captured as Sentry logs.
      consoleLoggingIntegration({ levels: ['error'] }),
    ],

    // Performance Monitoring — tiered sampling on the browser.
    // Mirrors the server tiers: payment > app > marketing.
    tracesSampler: (ctx) => {
      const name = ctx.name ?? '';
      const url =
        typeof window !== 'undefined' ? window.location.pathname : name;

      // Drop noise
      if (/\/(_next\/static|favicon|robots\.txt|sitemap)/.test(url)) {
        return 0;
      }

      // Payment flows on the browser (Moyasar checkout, payment-confirm pages)
      if (/\/payment(?:\/|$)|moyasar/i.test(url)) {
        return 1.0;
      }

      // Booking / package flows
      if (/\/(?:book|booking|packages?|classes?)(?:\/|$)/.test(url)) {
        return 0.2;
      }

      // Authenticated app pages
      if (/\/home(?:\/|$)/.test(url)) {
        return 0.05;
      }

      // Marketing / public — heartbeat
      return 0.02;
    },

    // Session Replay — only capture replays for sessions where an error
    // occurred. No background sampling of healthy sessions.
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0,

    // Filter out browser extension noise, transient network errors, and
    // payment SDK issues that are not actionable
    beforeSend: (event) => {
      // Check message
      if (isIgnoredClientError(event.message)) {
        return null;
      }

      // Check all exception values (some errors have multiple frames)
      const exceptions = event.exception?.values ?? [];

      for (const ex of exceptions) {
        if (isIgnoredClientError(ex.value) || isIgnoredClientError(ex.type)) {
          return null;
        }
      }

      return event;
    },

    // Note: if you want to override the automatic release value, do not set a
    // `release` value here - use the environment variable `SENTRY_RELEASE`, so
    // that it will also get attached to your source maps,
    ...props,
  });
}
